<template>
  <div>
    <v-btn icon color="primary" @click="$refs.fileInput.$refs.input.click()">
      <v-icon dark> mdi-attachment </v-icon>
    </v-btn>

    <v-file-input
      v-show="false"
      v-model="input"
      ref="fileInput"
      multiple
      accept="image/jpeg,image/png,video/mp4"
      @change="handleChange"
    />
  </div>
</template>

<script>
export default {
  name: "AttachmentsInput",

  props: {
    value: Array,
  },

  data() {
    return {
      input: [],
      processing: false
    };
  },

  methods: {
    async handleChange(files) {
      if (!files || !files.length || this.value.length >= 10 || this.processing) {
        return;
      }

      this.processing = true;
      try {
        let newFiles = await this.mapNewFiles(files);
        let attachments = [...this.value, ...newFiles];

        if (attachments.length > 10) {
          attachments = attachments.slice(0, 10);
        }

        this.$emit("input", attachments);
      } finally {
        this.input = [];
        this.processing = false;
      }
    },
    
    async mapNewFiles(files) {
      const mappedFiles = await Promise.all(
        files.map(async (e) => {
          // Create basic file object
          const file = {
            name: e.name,
            type: e.type,
          };

          // For images, process them for better display but maintain quality
          if (file.type.startsWith("image/")) {
            const processedImage = await this.processImage(e);
            file.url = processedImage.url;
          } 
          // For videos, create a thumbnail
          else if (file.type.startsWith("video/")) {
            file.url = URL.createObjectURL(e);
            const thumbnail = await this.generateVideoThumbnail(file.url);
            file.thumbnail = thumbnail;
          }

          return file;
        })
      );

      return mappedFiles;
    },
    
    async processImage(file) {
      return new Promise((resolve) => {
        // Create a URL for the image file
        const url = URL.createObjectURL(file);
        
        // Load the image to get dimensions
        const img = new Image();
        
        img.onload = () => {
          // Only resize if the image is very large
          if (img.width > 1920 || img.height > 1920) {
            // Create canvas for resizing
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            
            // Calculate new dimensions while maintaining aspect ratio
            let newWidth, newHeight;
            const aspectRatio = img.width / img.height;
            
            if (img.width > img.height) {
              newWidth = Math.min(img.width, 1920);
              newHeight = Math.round(newWidth / aspectRatio);
            } else {
              newHeight = Math.min(img.height, 1920);
              newWidth = Math.round(newHeight * aspectRatio);
            }
            
            // Resize using canvas
            canvas.width = newWidth;
            canvas.height = newHeight;
            ctx.drawImage(img, 0, 0, newWidth, newHeight);
            
            // Convert to high-quality JPEG
            const dataUrl = canvas.toDataURL('image/jpeg', 0.95);
            resolve({ url: dataUrl });
          } else {
            // If image is already reasonable size, use as-is
            resolve({ url });
          }
        };
        
        // Handle load errors
        img.onerror = () => {
          // If there's an error, just use the original file
          resolve({ url });
        };
        
        // Set the source to load the image
        img.src = url;
      });
    },
    
    generateVideoThumbnail(url) {
      return new Promise((resolve) => {
        const video = document.createElement("video");
        video.src = url;

        video.addEventListener("loadeddata", () => {
          video.currentTime = 1; // Seek to 1 second into the video
        });

        video.addEventListener("seeked", () => {
          const canvas = document.createElement("canvas");
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          const context = canvas.getContext("2d");
          context.drawImage(video, 0, 0, canvas.width, canvas.height);
          const thumbnail = canvas.toDataURL("image/jpeg", 0.9);
          resolve(thumbnail);
        });
      });
    },
  },
};
</script>
